import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-hot-toast';
import { FaTrophy, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import apiService from '../services/api';

interface Hit {
  id: string;
  url: string;
  path: string;
  discoveredAt: string;
  services: string[];
  credentials: any;
  status: 'valid' | 'invalid';
  isNew: boolean;
}

interface GlobalWebSocketManagerRef {
  toggleSound: () => void;
}

const GlobalWebSocketManager = forwardRef<GlobalWebSocketManagerRef>((props, ref) => {
  const wsRef = useRef<WebSocket | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const soundEnabled = useRef<boolean>(true);

  // Initialiser l'audio pour les notifications
  useEffect(() => {
    audioRef.current = new Audio('/notification.mp3');
    audioRef.current.volume = 0.5;
  }, []);

  // Fonction pour jouer le son de notification
  const playNotificationSound = () => {
    if (soundEnabled.current && audioRef.current) {
      audioRef.current.play().catch(() => {
        // Ignorer les erreurs si l'audio ne peut pas être joué
      });
    }
  };

  // Fonction pour obtenir l'icône du service
  const getServiceIcon = (service: string) => {
    switch (service) {
      case 'AWS': return '☁️';
      case 'SendGrid': return '📧';
      case 'Brevo': return '💌';
      case 'Sparkpost': return '⚡';
      case 'Mailgun': return '🔫';
      case 'Twilio': return '📱';
      case 'SMTP': return '📨';
      default: return '🔑';
    }
  };

  // Connexion WebSocket globale
  useEffect(() => {
    try {
      const ws = apiService.createHitsWebSocketConnection();
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('🔗 WebSocket global connecté pour les hits');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_hit' && data.hit.status === 'valid') {
            // Jouer le son et afficher la notification pour les hits valides
            playNotificationSound();
            
            // Afficher une notification toast
            toast.success(
              <div className="flex items-center space-x-3">
                <FaTrophy className="text-yellow-400 text-xl" />
                <div>
                  <div className="font-semibold text-white">Nouveau Hit Valide !</div>
                  <div className="text-sm text-gray-300">{data.hit.url}</div>
                  <div className="text-xs text-gray-400">
                    {data.hit.services.map((service: string) => getServiceIcon(service) + ' ' + service).join(', ')}
                  </div>
                </div>
              </div>,
              {
                duration: 8000,
                position: 'top-right',
                style: {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                },
              }
            );
          }
        } catch (error) {
          console.error('Erreur parsing WebSocket message global:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('Erreur WebSocket global:', error);
      };

      ws.onclose = () => {
        console.log('WebSocket global déconnecté');
        // Tentative de reconnexion automatique
        setTimeout(() => {
          if (wsRef.current?.readyState === WebSocket.CLOSED) {
            console.log('🔄 Tentative de reconnexion WebSocket global...');
            // La reconnexion sera gérée par le prochain useEffect
          }
        }, 3000);
      };

      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };
    } catch (error) {
      console.error('Erreur connexion WebSocket global:', error);
    }
  }, []);

  // Fonction pour activer/désactiver le son (accessible globalement)
  const toggleSound = () => {
    soundEnabled.current = !soundEnabled.current;
    if (soundEnabled.current) {
      toast.success('Son activé', { duration: 2000 });
    } else {
      toast.success('Son désactivé', { duration: 2000 });
    }
  };

  // Exposer la fonction globalement
  useImperativeHandle(ref, () => ({
    toggleSound
  }));

  return null; // Ce composant ne rend rien visuellement
});

export default GlobalWebSocketManager; 